import { api as wiredApi } from './index';

/**
 * InGo's maps and journeys for the app (the mobile plan's M6 to M8; InGo
 * ADR 0005 and 0013): which map to draw, the road between two pins, where a
 * delivery's rider is now, and the rider's own positions while carrying a
 * job. Built from its client so a test can hand it a fake; `mapsApi` below
 * is the app's wired copy.
 */
export function createMapsApi({ api }) {
  let config = null;

  return {
    /**
     * `GET maps/config`: the style, where to open, the credit to show, how
     * often to ask for a rider's position. Asked once a session; a failed
     * ask is asked again next time.
     */
    mapConfig() {
      config ??= api.get('/maps/config').then(
        ({ data }) => data,
        (error) => {
          config = null;
          throw error;
        },
      );

      return config;
    },

    /**
     * `POST routes`: the road through two or more pins, or null when routing
     * is off or the engine found none. `points` are `{ lat, lng }`.
     */
    async route(points) {
      const { data } = await api.post('/routes', {
        points: points.map((p) => ({ latitude: p.lat, longitude: p.lng })),
      });

      return data?.route ?? null;
    },

    /** `GET journeys/delivery/{id}/tracking`: the rider's last position, where they are heading, the estimate. */
    async tracking(deliveryId) {
      const { data } = await api.get(`/journeys/delivery/${encodeURIComponent(deliveryId)}/tracking`);

      return data;
    },

    /**
     * `POST journeys/delivery/{id}/locations`: the carrying rider's positions,
     * oldest first. Never retried by the client: a position a minute late is
     * worth less than the next one, which follows in seconds.
     */
    async sendPositions(deliveryId, pings) {
      const { data } = await api.post(`/journeys/delivery/${encodeURIComponent(deliveryId)}/locations`, { pings });

      return data?.accepted ?? 0;
    },
  };
}

export const mapsApi = createMapsApi({ api: wiredApi });
